import { CircleAlert, Info } from 'lucide-react';

import { formatShortDate } from './format';
import { InfoTip } from './info-tip';
import type { SectionStatus } from './types';

export type ChartSeries<Row> = {
  key: keyof Row & string;
  label: string;
  color: string;
};

/** Stacked daily bar chart rendered as plain SVG (no chart dependency). */
export function DailyBars<Row extends { date: string }>(props: {
  data: Row[];
  series: ChartSeries<Row>[];
  formatValue: (value: number) => string;
  height?: number;
  ariaLabel: string;
}) {
  const { data, series, formatValue, height = 132, ariaLabel } = props;
  const totals = data.map((row) => series.reduce((sum, item) => sum + Math.max(0, Number(row[item.key]) || 0), 0));
  const max = Math.max(1, ...totals);
  const slot = 10;
  const gap = data.length > 60 ? 1 : 2;
  const width = Math.max(1, data.length * slot);
  const labels = data.length > 0 ? [0, Math.floor((data.length - 1) / 2), data.length - 1] : [];

  return (
    <figure className="chart">
      <svg
        className="chart-svg"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={ariaLabel}
        style={{ height }}
      >
        <line x1="0" x2={width} y1={height - 0.5} y2={height - 0.5} className="chart-axis" />
        {data.map((row, index) => {
          let offset = height;
          return (
            <g key={row.date}>
              <title>
                {`${formatShortDate(row.date)}: ${series.map((item) => `${item.label} ${formatValue(Number(row[item.key]) || 0)}`).join(', ')}`}
              </title>
              <rect x={index * slot} y={0} width={slot} height={height} fill="transparent" />
              {series.map((item) => {
                const value = Math.max(0, Number(row[item.key]) || 0);
                const barHeight = (value / max) * (height - 4);
                offset -= barHeight;
                return value > 0 ? (
                  <rect
                    key={item.key}
                    x={index * slot + gap / 2}
                    y={offset}
                    width={slot - gap}
                    height={barHeight}
                    fill={item.color}
                    rx={1.5}
                  />
                ) : null;
              })}
            </g>
          );
        })}
      </svg>
      <figcaption className="chart-caption">
        <div className="chart-dates">
          {labels.map((index, position) => (
            <span key={`${index}-${position}`}>{formatShortDate(data[index].date)}</span>
          ))}
        </div>
        <div className="chart-legend">
          {series.map((item) => (
            <span key={item.key}>
              <i style={{ background: item.color }} aria-hidden="true" />
              {item.label}
            </span>
          ))}
          <span className="chart-peak">Peak {formatValue(Math.max(0, ...totals))}</span>
        </div>
      </figcaption>
    </figure>
  );
}

export type BarListItem = { label: string; value: number; display: string; detail?: string };

/** Horizontal ranked bars for breakdowns and funnels. */
export function BarList(props: { items: BarListItem[]; emptyText?: string; tone?: 'fern' | 'blue' | 'amber' }) {
  const { items, emptyText = 'No data for this range.', tone = 'fern' } = props;
  if (items.length === 0) return <p className="empty-state">{emptyText}</p>;
  const max = Math.max(1, ...items.map((item) => item.value));
  return (
    <ol className="bar-list">
      {items.map((item) => (
        <li key={item.label}>
          <div className="bar-list-row">
            <span className="bar-list-label" title={item.label}>{item.label}</span>
            <strong>{item.display}</strong>
          </div>
          <div className="bar-track" aria-hidden="true">
            <div className={`bar-fill bar-fill-${tone}`} style={{ width: `${Math.max(2, (item.value / max) * 100)}%` }} />
          </div>
          {item.detail ? <small>{item.detail}</small> : null}
        </li>
      ))}
    </ol>
  );
}

const STATUS_TITLES: Record<Exclude<SectionStatus, 'ok'>, string> = {
  empty: 'No data yet',
  not_configured: 'Not configured',
  not_tracked: 'Not tracked yet',
  error: 'Source unavailable',
};

/** Explains why a section has no data; renders nothing for healthy sections without a note. */
export function SourceNotice(props: { status: SectionStatus; message: string | null }) {
  const { status, message } = props;
  if (status === 'ok') {
    return message ? <p className="source-note"><Info size={14} aria-hidden="true" />{message}</p> : null;
  }
  return (
    <div className={`source-notice source-notice-${status}`} role={status === 'error' ? 'alert' : 'status'}>
      <CircleAlert size={16} aria-hidden="true" />
      <div>
        <strong>{STATUS_TITLES[status]}</strong>
        {message ? <p>{message}</p> : null}
      </div>
    </div>
  );
}

export function Metric(props: { label: string; value: string; hint?: string; tone?: 'default' | 'muted'; info?: string }) {
  return (
    <div className={`metric ${props.tone === 'muted' ? 'metric-muted' : ''}`}>
      <span>
        {props.label}
        {props.info ? <InfoTip label={props.label} text={props.info} /> : null}
      </span>
      <strong>{props.value}</strong>
      {props.hint ? <small>{props.hint}</small> : null}
    </div>
  );
}

/** Uppercase section label inside a card, with an optional explanation bubble. */
export function SubHeading(props: { title: string; info?: string }) {
  return (
    <h4 className="card-subheading">
      {props.title}
      {props.info ? <InfoTip label={props.title} text={props.info} /> : null}
    </h4>
  );
}

export type SplitSegment = { label: string; value: number; color: string; detail?: string };

/** A 100% bar comparing parts of a whole, with a legend of counts and shares. */
export function SplitBar(props: { segments: SplitSegment[]; ariaLabel: string; formatValue: (value: number) => string }) {
  const total = props.segments.reduce((sum, segment) => sum + Math.max(0, segment.value), 0);
  const share = (value: number) => (total > 0 ? Math.max(0, value) / total : 0);
  return (
    <figure className="split">
      <div className="split-bar" role="img" aria-label={`${props.ariaLabel}: ${props.segments
        .map((segment) => `${segment.label} ${Math.round(share(segment.value) * 100)}%`).join(', ')}`}>
        {total > 0 ? props.segments.map((segment) => share(segment.value) > 0 ? (
          <span key={segment.label} style={{ width: `${share(segment.value) * 100}%`, background: segment.color }}>
            {share(segment.value) >= 0.12 ? `${Math.round(share(segment.value) * 100)}%` : ''}
          </span>
        ) : null) : <span className="split-empty">No data for this range</span>}
      </div>
      <figcaption className="split-legend">
        {props.segments.map((segment) => (
          <div key={segment.label}>
            <i style={{ background: segment.color }} aria-hidden="true" />
            <span>{segment.label}</span>
            <strong>{props.formatValue(segment.value)}</strong>
            <small>{total > 0 ? `${Math.round(share(segment.value) * 100)}%` : '—'}{segment.detail ? ` · ${segment.detail}` : ''}</small>
          </div>
        ))}
      </figcaption>
    </figure>
  );
}

export type LengthBucket = { label: string; active: number; ended: number };

/** Vertical histogram of subscription length, stacked into still-active and ended subscriptions. */
export function LengthBars(props: { buckets: LengthBucket[]; colors: { active: string; ended: string } }) {
  const max = Math.max(1, ...props.buckets.map((bucket) => bucket.active + bucket.ended));
  return (
    <figure className="length-chart">
      <div className="length-bars" role="img" aria-label={`Subscriptions by length: ${props.buckets
        .map((bucket) => `${bucket.label} ${bucket.active + bucket.ended}`).join(', ')}`}>
        {props.buckets.map((bucket) => {
          const total = bucket.active + bucket.ended;
          return (
            <div key={bucket.label} className="length-column" title={`${bucket.label}: ${bucket.active} active, ${bucket.ended} ended`}>
              <span className="length-count">{total || ''}</span>
              <div className="length-stack" style={{ height: `${(total / max) * 100}%` }}>
                {bucket.ended ? <span style={{ flexGrow: bucket.ended, background: props.colors.ended }} /> : null}
                {bucket.active ? <span style={{ flexGrow: bucket.active, background: props.colors.active }} /> : null}
              </div>
              <span className="length-label">{bucket.label}</span>
            </div>
          );
        })}
      </div>
      <figcaption className="chart-legend">
        <span><i style={{ background: props.colors.active }} aria-hidden="true" />Still subscribed</span>
        <span><i style={{ background: props.colors.ended }} aria-hidden="true" />Ended</span>
      </figcaption>
    </figure>
  );
}
